import {
  Component,
  OnInit,
  Input,
  ViewChild,
  ElementRef,
  OnChanges,
  SimpleChanges,
  Output,
  EventEmitter,
  HostListener,
} from '@angular/core';
import { Observable } from 'rxjs';
import { EfoXlsService } from '../../services/efo-xls.service';
import { DataTableDirective } from 'angular-datatables';
import * as _ from 'lodash';

@Component({
  selector: 'egi-table',
  templateUrl: './egi-table.component.html',
  styleUrls: ['./egi-table.component.css'],
})
export class EgiTableComponent implements OnInit, OnChanges {
  @Input() dataFunc?: (...params: any[]) => Observable<any>;
  @Input() downloadFunc?: (...params: any[]) => Observable<any>;

  @Input() params: any[] = [];
  @Input() columns: DataTables.ColumnSettings[];
  @Input() server: boolean = false;
  @Input() dtOptions: DataTables.Settings = {};
  @Input() colRemove: boolean = false;
  @Input() exportXLSX: boolean = false;
  @Input() searching: boolean = false;
  @Input() delete: boolean = false;
  @Input() pageLength: number = 10;
  @Input() forceReload: boolean = false;
  @Input() selectedColumns: DataTables.ColumnSettings[] = [];
  @Input() fileName: string = 'Data';
  @Input() startingOrder: [number | string, 'asc' | 'desc'] = [0, 'asc'];

  @Output() rowClick: EventEmitter<any> = new EventEmitter<any>();
  @Output() deleteClick: EventEmitter<any> = new EventEmitter<any>();

  @ViewChild('dtTableElement') dtTableElement: ElementRef;
  @ViewChild(DataTableDirective, { static: false })
  dtElement: DataTableDirective;

  @Input() data?: any[];
  tableParameters: DataTableParameters;
  loading: boolean = false;
  totalRecords: any;
  contextMenuPosition: { x: number; y: number } = undefined;
  contextMenuData: any = undefined;
  selectedColumnsCopy: DataTables.ColumnSettings[] = [];
  includeHiddenColumns = false;
  downloading = false;
  keys = Object.keys;

  constructor(private efoXls: EfoXlsService) {}

  async ngOnInit() {
    if (this.selectedColumns.length === 0)
      this.selectedColumnsCopy = [...this.columns];
    else
      this.selectedColumnsCopy = [
        ...this.columns.filter((col) =>
          this.selectedColumns.map((col2) => col2.title).includes(col.title),
        ),
      ];
    if (!this.server) {
      //if server side is false
      await this.getData();
    } else {
      if (Object.keys(this.dtOptions).length === 0)
        this.dtOptions = {
          scrollX: true,
          order: this.startingOrder,
          pagingType: 'full_numbers',
          pageLength: this.pageLength,
          serverSide: true,
          searching: this.searching,
          processing: true,
          searchDelay: 1000,
          columns: this.selectedColumnsCopy,
          ajax: (dataTablesParameters: any, callback) => {
            this.tableParameters = dataTablesParameters;
            dataTablesParameters.columns = JSON.stringify(
              dataTablesParameters.columns,
            );
            dataTablesParameters.order = JSON.stringify(
              dataTablesParameters.order,
            );
            dataTablesParameters.search = JSON.stringify(
              dataTablesParameters.search,
            );
            this.dataFunc(dataTablesParameters, ...this.params).subscribe(
              (resp) => {
                this.totalRecords = resp.recordsTotal;
                callback({
                  recordsTotal: resp.recordsTotal,
                  recordsFiltered: resp.recordsFiltered,
                  data: resp.data,
                });
              },
            );
          },
          rowCallback: (row: Node, data: any, index: number) => {
            const self = this;
            $('td', row).off('click');
            $('td', row).on('click', () => {
              self.rowClick.emit(data);
            });
            return row;
          },
        };
    }
  }

  ngAfterViewInit() {
    this.addRightClick();
  }

  addRightClick() {
    if (this.delete) {
      const tableElement = this.dtTableElement.nativeElement;
      tableElement.addEventListener('contextmenu', (event: MouseEvent) => {
        event.preventDefault(); // Prevents the default browser context menu
        const target = event.target as HTMLElement;
        const rowElement = target.closest('tr'); // Find the nearest tr ancestor

        if (rowElement) {
          const dataTable = $(tableElement).DataTable();
          this.contextMenuData = dataTable.row(rowElement).data();
          this.contextMenuPosition = {
            x: event.clientX,
            y: event.clientY,
          };
          return false; // to prevent the browser's default context menu
        }
      });
    }
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (this.contextMenuPosition && !this.isClickInsideContextMenu(event)) {
      this.contextMenuPosition = undefined; // Close the context menu
      this.contextMenuData = undefined;
    }
  }

  private isClickInsideContextMenu(event: MouseEvent): boolean {
    const contextMenu = document.querySelector('.context-menu');
    return contextMenu.contains(event.target as Node);
  }

  onDelete(): void {
    this.deleteClick.emit(this.contextMenuData);
    this.contextMenuData = undefined;
    this.contextMenuPosition = undefined;
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.data && !changes.data.firstChange) {
      this.dtOptions.data = this.data;
      this.rerender();
    }
    if (
      changes.forceReload &&
      !changes.forceReload.firstChange &&
      this.forceReload === true
    ) {
      this.rerender();
    }
  }

  downloadFile = async () => {
    this.downloading = true;
    if (this.downloadFunc) {
      await this.downloadFunc(...this.params).toPromise();
      this.downloading = false;
      return;
    }
    if (this.server) {
      this.tableParameters.start = 0;
      this.tableParameters.length = this.totalRecords;
      this.data = (
        await this.dataFunc(this.tableParameters, ...this.params).toPromise()
      ).data;
    }

    let filteredData = [];
    if (this.includeHiddenColumns) filteredData = this.data;
    else
      filteredData = this.data.map((item: any) => {
        let filteredItem: any = {};
        this.selectedColumnsCopy.forEach((column: any) => {
          const columnName = column.data;
          if (item.hasOwnProperty(columnName)) {
            filteredItem[columnName] = item[columnName];
          }
        });
        return filteredItem;
      });

    if (!this.server) {
      await this.dtElement.dtInstance.then((dtInstance) => {
        const searchValue = dtInstance.search();
        if (searchValue) {
          filteredData = filteredData.filter((obj) =>
            Object.values(obj).some((value) =>
              String(value).toLowerCase().includes(searchValue.toLowerCase()),
            ),
          );
        }
      });
    }
    this.efoXls.exportDataToXlsx(
      this.fileName,
      { data: filteredData },
      [],
      false,
    );
    this.downloading = false;
  };

  getData = async () => {
    if (!this.data) {
      this.loading = true;
      this.data = await this.dataFunc(...this.params).toPromise();
    }
    this.initializeDataTable();
    this.loading = false;
  };

  initializeDataTable = () => {
    this.dtOptions = {
      searching: this.searching,
      scrollX: true,
      order: this.startingOrder,
      data: this.data,
      columns: this.selectedColumnsCopy,
      rowCallback: (row: Node, data: any, index: number) => {
        const self = this;
        $('td', row).off('click');
        $('td', row).on('click', () => {
          self.rowClick.emit(data);
        });
        return row;
      },
    };
  };

  handleColumns = _.debounce(() => {
    this.selectedColumnsCopy.sort((a, b) => {
      //keeps columns in the same order
      const indexA = this.columns.indexOf(a);
      const indexB = this.columns.indexOf(b);
      return indexA - indexB;
    });
    this.dtOptions.columns = [...this.selectedColumnsCopy];
    this.rerender();
  }, 750);

  rerender = () => {
    this.dtElement.dtInstance.then((dtInstance: DataTables.Api) => {
      dtInstance.destroy();
      this.dtElement.dtOptions = this.dtOptions;
      this.displayTable(this.dtTableElement);
    });
  };

  private displayTable(renderIn: ElementRef): void {
    this.dtElement.dtInstance = new Promise((resolve, reject) => {
      Promise.resolve(this.dtOptions)
        .then((dtOptions) => {
          setTimeout(() => {
            $(renderIn.nativeElement).empty();
            var dt = $(renderIn.nativeElement).DataTable(dtOptions);
            resolve(dt);
            this.addRightClick();
          });
        })
        .catch((error) => reject(error));
    });
  }
}
interface DataTableParameters {
  draw: number;
  columns: any[];
  order: any[];
  start: number;
  length: number;
  search: {
    value: string;
    regex: boolean;
  };
}
